import { useTour } from './TourContext';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, X, PartyPopper } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';

const TourTooltip = () => {
  const {
    currentStep,
    totalSteps,
    currentTourStep,
    nextStep,
    prevStep,
    skipTour,
    completeTour
  } = useTour();

  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  if (!currentTourStep) return null;

  const isFirstStep = currentStep === 0;
  const isLastStep = currentTourStep.isLast;

  const handleSkipClick = () => {
    setShowSkipConfirm(true);
  };

  const confirmSkip = () => {
    setShowSkipConfirm(false);
    skipTour();
  };

  return (
    <>
      <div 
        className="w-[350px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden"
        data-testid="tour-tooltip"
      >
        {/* Header */}
        <div className="bg-zinc-800 px-4 py-3 flex items-center justify-between border-b border-zinc-700">
          <span className="text-sm font-medium text-zinc-400">
            Step {currentStep + 1} of {totalSteps}
          </span>
          <button
            onClick={handleSkipClick}
            className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
            data-testid="tour-skip-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="flex items-start gap-3">
            {isLastStep && (
              <div className="w-10 h-10 rounded-full bg-[#ffd108]/20 flex items-center justify-center flex-shrink-0">
                <PartyPopper className="h-5 w-5 text-[#ffd108]" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-white text-lg mb-2">
                {currentTourStep.title}
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                {currentTourStep.content}
              </p>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-5 pb-2">
          <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#ffd108] transition-all duration-300"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={prevStep}
            disabled={isFirstStep}
            className="text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-30"
            data-testid="tour-prev-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          <Button
            size="sm"
            onClick={isLastStep ? completeTour : nextStep}
            className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90 font-medium"
            data-testid="tour-next-btn"
          >
            {isLastStep ? (
              "Get Started"
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Skip Confirmation Dialog - rendered via portal with high z-index */}
      {showSkipConfirm && createPortal(
        <div className="fixed inset-0 z-[10001]">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60"
            onClick={() => setShowSkipConfirm(false)}
          />
          {/* Dialog */}
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 shadow-2xl">
              <h2 className="text-lg font-semibold text-white mb-2">Skip the tour?</h2>
              <p className="text-zinc-400 text-sm mb-6">
                You can restart the tour anytime from the Help Centre.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowSkipConfirm(false)}
                  className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700"
                >
                  Continue Tour
                </Button>
                <Button
                  onClick={confirmSkip}
                  className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90"
                >
                  Skip Tour
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TourTooltip;
